import Link from "next/link";
import { NextSeo } from "next-seo";
import { LandingHeader } from "@/components/landing/landing-header";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <NextSeo
        canonical="https://www.vacwork.app/terms"
        description="Read the terms of service for using VacWork. Understand your rights and responsibilities as a user."
        title="Terms of Service | VacWork"
      />
      {/* Use the same floating header from landing page */}
      <LandingHeader showSectionLinks={false} />

      {/* Main Content with top padding to account for fixed header */}
      <main className="container mx-auto max-w-4xl px-4 pt-24 pb-12">
        <h1 className="mb-8 font-light text-3xl md:text-4xl">
          Terms of Service
        </h1>

        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <p className="mb-6 text-muted-foreground">
            Effective Date: 17 June 2025
          </p>

          <section className="mb-10">
            <p className="mb-4">
              Welcome to VacWork ("VacWork", "we", "us", or "our"). These Terms
              of Service ("Terms") govern your use of our website, platform, and
              services ("Services"). By accessing or using VacWork, you agree to
              be bound by these Terms. If you do not agree, do not use our
              Services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              1. Use of Services
            </h2>
            <p className="mb-4">
              You may use VacWork solely for lawful purposes and in accordance
              with these Terms. You agree not to:
            </p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                Use the platform to post false, misleading, or fraudulent
                information.
              </li>
              <li className="mb-2">
                Attempt to access or tamper with non-public areas of the
                platform or its systems.
              </li>
              <li>
                Use automated systems (e.g., bots) to access or use our Services
                without express permission.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              2. User Accounts and Responsibilities
            </h2>
            <p className="mb-4">
              To access certain features, you may be required to create an
              account. You are responsible for safeguarding your account and for
              all activities under it. VacWork is not liable for any loss or
              damage arising from your failure to maintain account security.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              3. Content Ownership and Licensing
            </h2>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                You retain ownership of any content you submit through VacWork,
                such as CVs or job listings.
              </li>
              <li className="mb-2">
                By submitting content, you grant VacWork a non-exclusive,
                worldwide, royalty-free license to use, display, and distribute
                it solely to operate and promote the platform.
              </li>
              <li>
                You represent that your content does not violate any third-party
                rights.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              4. Platform Availability
            </h2>
            <p>
              We aim to provide continuous access to VacWork but do not
              guarantee uninterrupted or error-free operation. We may suspend or
              terminate Services temporarily or permanently without prior
              notice.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              5. Limitation of Liability
            </h2>
            <p className="mb-4">
              VacWork, its founders, and affiliates will not be liable for any
              indirect, incidental, special, or consequential damages arising
              from:
            </p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                Your use or inability to use the platform.
              </li>
              <li className="mb-2">Errors or omissions in content.</li>
              <li className="mb-2">
                Unauthorised access or alterations to your data.
              </li>
            </ul>
            <p>
              Use of the platform is at your own risk. VacWork is provided "as
              is" and "as available."
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              6. Indemnification
            </h2>
            <p className="mb-4">
              You agree to indemnify and hold harmless VacWork, its founders,
              employees, and affiliates from any claims, liabilities, damages,
              or costs (including legal fees) arising out of your:
            </p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">Use of the platform,</li>
              <li className="mb-2">Violation of these Terms,</li>
              <li>Infringement of any rights of another party.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">7. Privacy</h2>
            <p>
              Your use of the platform is also governed by our{" "}
              <Link className="text-primary hover:underline" href="/privacy">
                Privacy Policy
              </Link>
              , which outlines how we collect and handle user data.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              8. Modifications
            </h2>
            <p>
              We may update these Terms at any time. Continued use of the
              platform after changes constitutes acceptance of the revised
              Terms. The latest version will always be available on our site.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              9. Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of the Republic of South
              Africa. Any disputes arising from these Terms shall be resolved in
              the courts of South Africa.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">10. Contact</h2>
            <p>For questions regarding these Terms, please contact us at:</p>
            <p className="mt-2">
              <a href="mailto:support@vacwork.com">📧 support@vacwork.app</a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
